import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Container,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { motion } from 'framer-motion';
import { colors } from '../theme';
import { menuItem } from '../utils/animations';

/**
 * HEURÍSTICAS APLICADAS NO HEADER:
 *
 * #1 - Visibilidade do status do sistema:
 *   - Scroll spy mostra seção ativa
 *   - Header muda de aparência ao rolar
 *
 * #2 - Correspondência entre sistema e mundo real:
 *   - Navegação intuitiva e familiar
 *
 * #4 - Consistência e padrões:
 *   - Estados hover/active consistentes
 *
 * #7 - Flexibilidade e eficiência de uso:
 *   - Acesso rápido às seções
 *   - Menu mobile responsivo
 *
 * #8 - Design estético e minimalista:
 *   - Visual limpo e profissional
 */

const navItems = [
  { label: 'Início', href: '#inicio' },
  { label: 'Produtos', href: '#produtos' },
  { label: 'Sobre', href: '#sobre' },
  { label: 'Parceiros', href: '#parceiros' },
  { label: 'Contato', href: '#contato' },
];

const Header: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('inicio');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Heurística #1: Detectar scroll e mudar aparência do header
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Heurística #1: Scroll spy para mostrar seção ativa
  useEffect(() => {
    const handleScrollSpy = () => {
      const sections = navItems.map((item) => ({
        id: item.href.substring(1),
        element: document.querySelector(item.href),
      }));

      const scrollPosition = window.scrollY + 100;

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (section.element) {
          const offsetTop = (section.element as HTMLElement).offsetTop;
          if (scrollPosition >= offsetTop) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScrollSpy);
    handleScrollSpy();
    return () => window.removeEventListener('scroll', handleScrollSpy);
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavClick = (href: string) => {
    setMobileOpen(false);
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const drawer = (
    <Box
      sx={{
        backgroundColor: colors.blackMatte,
        height: '100%',
        pt: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          px: 2,
          pb: 2,
        }}
      >
        <IconButton
          onClick={handleDrawerToggle}
          sx={{ color: colors.beige }}
          aria-label="Fechar menu"
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <List>
        {navItems.map((item, i) => (
          <motion.div
            key={item.label}
            custom={i}
            initial="closed"
            animate={mobileOpen ? 'open' : 'closed'}
            variants={menuItem}
          >
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => handleNavClick(item.href)}
                sx={{
                  py: 2,
                  px: 3,
                  backgroundColor:
                    activeSection === item.href.substring(1)
                      ? `${colors.agedGold}20`
                      : 'transparent',
                  borderLeft:
                    activeSection === item.href.substring(1)
                      ? `4px solid ${colors.agedGold}`
                      : '4px solid transparent',
                }}
              >
                <ListItemText
                  primary={item.label}
                  sx={{
                    color:
                      activeSection === item.href.substring(1)
                        ? colors.agedGold
                        : colors.beige,
                    '& .MuiTypography-root': {
                      fontFamily: '"Cormorant Garamond", serif',
                      fontSize: '1.5rem',
                      fontWeight: 600,
                    },
                  }}
                />
              </ListItemButton>
            </ListItem>
          </motion.div>
        ))}
      </List>
    </Box>
  );

  return (
    <>
      <AppBar
        position="fixed"
        elevation={scrolled ? 4 : 0}
        component={motion.header}
        sx={{
          backgroundColor: scrolled
            ? `${colors.beige}f5`
            : colors.beige,
          borderBottom: `1px solid ${colors.agedGold}`,
          backdropFilter: scrolled ? 'blur(10px)' : 'none',
          transition: 'all 0.3s cubic-bezier(0.43, 0.13, 0.23, 0.96)',
        }}
      >
        <Container maxWidth="xl">
          <Toolbar
            sx={{
              justifyContent: 'space-between',
              minHeight: { xs: '64px', md: scrolled ? '70px' : '80px' },
              transition: 'min-height 0.3s cubic-bezier(0.43, 0.13, 0.23, 0.96)',
            }}
          >
            <Box
              component={motion.a}
              href="#inicio"
              onClick={(e) => {
                e.preventDefault();
                handleNavClick('#inicio');
              }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              sx={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <img
                src="/horizontal_brand.png"
                alt="Barba Barão"
                style={{
                  height: scrolled ? '40px' : '50px',
                  width: 'auto',
                  transition: 'height 0.3s cubic-bezier(0.43, 0.13, 0.23, 0.96)',
                }}
              />
            </Box>

            {isMobile ? (
              <IconButton
                color="inherit"
                aria-label="Abrir menu de navegação"
                edge="end"
                onClick={handleDrawerToggle}
                sx={{ color: colors.blackMatte }}
              >
                <MenuIcon sx={{ fontSize: '2rem' }} />
              </IconButton>
            ) : (
              <Box sx={{ display: 'flex', gap: 4 }}>
                {navItems.map((item) => {
                  const isActive = activeSection === item.href.substring(1);
                  return (
                    <Typography
                      key={item.label}
                      component={motion.a}
                      href={item.href}
                      onClick={(e) => {
                        e.preventDefault();
                        handleNavClick(item.href);
                      }}
                      whileHover={{ y: -2 }}
                      whileTap={{ scale: 0.95 }}
                      sx={{
                        color: isActive ? colors.darkWine : colors.blackMatte,
                        textDecoration: 'none',
                        fontSize: '1.1rem',
                        fontWeight: isActive ? 700 : 500,
                        position: 'relative',
                        cursor: 'pointer',
                        transition: 'all 0.3s cubic-bezier(0.43, 0.13, 0.23, 0.96)',
                        '&:after': {
                          content: '""',
                          position: 'absolute',
                          bottom: -4,
                          left: 0,
                          width: isActive ? '100%' : '0%',
                          height: '2px',
                          backgroundColor: colors.darkWine,
                          transition: 'width 0.3s cubic-bezier(0.43, 0.13, 0.23, 0.96)',
                        },
                        '&:hover': {
                          color: colors.darkWine,
                          '&:after': {
                            width: '100%',
                          },
                        },
                      }}
                    >
                      {item.label}
                    </Typography>
                  );
                })}
              </Box>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: '280px',
          },
        }}
      >
        {drawer}
      </Drawer>

      <Toolbar sx={{ minHeight: { xs: '64px', md: '80px' } }} />
    </>
  );
};

export default Header;
